import type { ReactNode } from "react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type {
  CustomerMinimal,
  FileEntry,
  Group,
  Suggestion,
} from "../state/types";
import { CustomerPicker } from "./CustomerPicker";
import { PdfFileCard } from "./PdfFileCard";

type GroupCardProps = {
  group: Group;
  files: Record<string, FileEntry>;
  customers: CustomerMinimal[];

  /**
   * When true, render the customer picker. Set to false to render the
   * card without a picker (e.g. a Phase-3 read-only summary of an
   * approved group).
   */
  showCustomerPicker: boolean;

  /**
   * Fully read-only card: no picker, no action buttons. Used to
   * summarise approved groups while editing other groups in Phase 3.
   */
  readOnly?: boolean;

  /**
   * Inline message rendered under the picker (e.g. duplicate-customer
   * warning).
   */
  customerPickerError?: string;

  onCustomerChange?: (customerId: string | null) => void;

  /**
   * Phase-2 actions. The button is only rendered if its callback is
   * provided. The parent should pass exactly the actions appropriate
   * for the current phase.
   */
  onApprove?: () => void;
  onMarkNeedsEdit?: () => void;

  /** Phase-3 action. */
  onDissolve?: () => void;

  /**
   * Optional per-file right-side action slot (e.g. a "Move" button in
   * Phase 3). When omitted, files render with no actions.
   */
  renderFileActions?: (fileId: string) => ReactNode;
};

type BadgeTone = "default" | "success" | "warning" | "info" | "muted";

function Badge({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: BadgeTone;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        tone === "success" &&
          "border-green-600 bg-green-50 text-green-800",
        tone === "warning" &&
          "border-amber-500 bg-amber-50 text-amber-900",
        tone === "info" && "border-primary bg-primary/10 text-primary",
        tone === "muted" &&
          "border-muted bg-muted/40 text-muted-foreground",
        tone === "default" && "border-muted bg-card text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Aggregate per-file suggestions into one ranked list for the group
 * card. Average score per customer across all files in the group.
 *
 * Per spec: group-level suggestions must be derived from current file
 * membership, never stored. We recompute on every render.
 */
function deriveGroupSuggestions(group: Group, files: Record<string, FileEntry>): Suggestion[] {
  type Agg = { totalScore: number; count: number; lastReason: string };
  const byCustomer = new Map<string, Agg>();
  for (const fileId of group.file_ids) {
    const entry = files[fileId];
    if (!entry) continue;
    for (const s of entry.suggestions) {
      const existing = byCustomer.get(s.customer_id);
      if (existing) {
        existing.totalScore += s.score;
        existing.count += 1;
        existing.lastReason = s.reason;
      } else {
        byCustomer.set(s.customer_id, {
          totalScore: s.score,
          count: 1,
          lastReason: s.reason,
        });
      }
    }
  }
  const aggregated: Suggestion[] = [];
  for (const [customerId, agg] of byCustomer.entries()) {
    aggregated.push({
      customer_id: customerId,
      score: agg.totalScore / agg.count,
      reason: agg.lastReason,
    });
  }
  aggregated.sort((a: Suggestion, b: Suggestion) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.customer_id < b.customer_id) return -1;
    if (a.customer_id > b.customer_id) return 1;
    return 0;
  });
  return aggregated.slice(0, 5);
}

function ConfidenceBadgeForGroup({ group }: { group: Group }) {
  if (group.source === "manual") return <Badge tone="info">Manual</Badge>;
  if (group.initial_bucket === 1) {
    return <Badge tone="success">High confidence</Badge>;
  }
  if (group.initial_bucket === 2) {
    return <Badge tone="warning">Plausible</Badge>;
  }
  return null;
}

function StateBadgeForGroup({ group }: { group: Group }) {
  switch (group.state) {
    case "approved":
      return <Badge tone="success">Approved</Badge>;
    case "needs_edit":
      return <Badge tone="warning">Needs manual edit</Badge>;
    case "manual":
      return <Badge tone="info">Manual</Badge>;
    case "suggested":
      return <Badge tone="muted">Suggested</Badge>;
    default: {
      const _exhaustive: never = group.state;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * Renders one wizard group. Shared by Phase 2 (suggested review) and
 * Phase 3 (manual grouping); the parent passes the actions appropriate
 * for the current phase via the optional callback props.
 *
 * Visual cues:
 *   - approved → green border
 *   - needs_edit → amber border
 *   - suggested/manual → neutral border
 *
 * Customer picker, action buttons, and per-file actions are all
 * optional — the card simply omits anything for which no callback was
 * provided.
 */
export function GroupCard({
  group,
  files,
  customers,
  showCustomerPicker,
  readOnly,
  customerPickerError,
  onCustomerChange,
  onApprove,
  onMarkNeedsEdit,
  onDissolve,
  renderFileActions,
}: GroupCardProps) {
  const groupSuggestions = useMemo(
    () => deriveGroupSuggestions(group, files),
    [group, files],
  );

  const customerById = useMemo((): Map<string, CustomerMinimal> => {
    const map = new Map<string, CustomerMinimal>();
    for (const c of customers) map.set(c.id, c);
    return map;
  }, [customers]);

  const selectedCustomer =
    group.selected_customer_id !== null
      ? customerById.get(group.selected_customer_id) ?? null
      : null;

  const borderClass =
    group.state === "approved"
      ? "border-green-600"
      : group.state === "needs_edit"
        ? "border-amber-500"
        : "border-border";

  const showApprove = !readOnly && typeof onApprove === "function";
  const showNeedsEdit = !readOnly && typeof onMarkNeedsEdit === "function";
  const showDissolve = !readOnly && typeof onDissolve === "function";
  const showAnyAction = showApprove || showNeedsEdit || showDissolve;
  const pickerDisabled = readOnly || !onCustomerChange;
  const renderPicker = showCustomerPicker && !readOnly;

  return (
    <div
      className={cn(
        "rounded-md border-2 bg-card p-3 flex flex-col gap-3",
        borderClass,
      )}
    >
      <div className="flex flex-row items-start justify-between gap-2 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex flex-row items-center gap-1.5 flex-wrap">
            <ConfidenceBadgeForGroup group={group} />
            <StateBadgeForGroup group={group} />
            <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
              {group.file_ids.length} PDF
              {group.file_ids.length === 1 ? "" : "s"}
            </span>
          </div>
          <div
            className={cn(
              "text-sm font-semibold break-words",
              selectedCustomer === null && "text-muted-foreground italic",
            )}
            title={selectedCustomer?.long_name}
          >
            {selectedCustomer
              ? selectedCustomer.long_name
              : "(no customer selected)"}
          </div>
        </div>
      </div>

      {renderPicker ? (
        <CustomerPicker
          selected_customer_id={group.selected_customer_id}
          suggestions={groupSuggestions}
          customers={customers}
          disabled={pickerDisabled}
          errorMessage={customerPickerError}
          onChange={(customerId: string | null) => {
            if (onCustomerChange) onCustomerChange(customerId);
          }}
        />
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
          Files
        </span>
        {group.file_ids.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            (no files in this group)
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {group.file_ids.map((fileId: string) => {
              const entry = files[fileId];
              if (!entry) return null;
              return (
                <li key={fileId}>
                  <PdfFileCard
                    filename={entry.file.name}
                    density="compact"
                    actions={
                      renderFileActions ? renderFileActions(fileId) : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showAnyAction ? (
        <div className="flex flex-row gap-2 justify-end flex-wrap">
          {showDissolve ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onDissolve}
            >
              Dissolve back to ungrouped
            </Button>
          ) : null}
          {showNeedsEdit ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onMarkNeedsEdit}
            >
              Needs manual edit
            </Button>
          ) : null}
          {showApprove ? (
            <Button
              size="sm"
              onClick={onApprove}
              disabled={group.selected_customer_id === null}
            >
              Approve group
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
